import { useState, useEffect, ReactNode } from 'react';
import { useTheme } from 'next-themes';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import CommandPalette from '@/components/layout/CommandPalette';
import AIChat from '@/components/chat/AIChat';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePresence } from '@/hooks/usePresence';
import { useUserRole } from '@/hooks/useUserRole';
import { sectionForView, VIEW_LABELS } from '@/lib/navigation';
import { useLocalization } from '@/hooks/useLocalization';

const DOCK_KEY = 'masira.askDock';

const readDockPref = (): boolean => {
  // Phones start with the panel closed; it opens on demand and is never persisted there.
  if (typeof window !== 'undefined' && window.innerWidth < 768) return false;
  try {
    const v = localStorage.getItem(DOCK_KEY);
    if (v === 'open') return true;
    if (v === 'closed') return false;
  } catch { /* storage unavailable: fall through to default */ }
  return typeof window !== 'undefined' && window.innerWidth >= 1280;
};

interface ConsoleLayoutProps {
  activeView: string;
  onViewChange: (view: string) => void;
  children: ReactNode;
}

/**
 * Shared chrome for every authenticated page: dark nav sidebar, header, content
 * and the docked "Ask Masira" panel. Used by the main shell (Index) and the
 * standalone routes (AppShell) so the experience is identical everywhere.
 */
const ConsoleLayout = ({ activeView, onViewChange, children }: ConsoleLayoutProps) => {
  const { t } = useLocalization();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(readDockPref);
  const { onlineUsers } = usePresence(activeView);
  const { canAccess } = useUserRole();
  const [flowRequest, setFlowRequest] = useState<{ templateId?: string; nonce: number }>();

  // Other screens (e.g. Reports) can ask the assistant to start the report flow.
  useEffect(() => {
    const onStart = (e: Event) => {
      const templateId = (e as CustomEvent<{ templateId?: string }>).detail?.templateId;
      setDockOpen(true);
      setFlowRequest({ templateId, nonce: Date.now() });
    };
    window.addEventListener('masira:start-report-flow', onStart);
    return () => window.removeEventListener('masira:start-report-flow', onStart);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    try { localStorage.setItem(DOCK_KEY, dockOpen ? 'open' : 'closed'); } catch { /* ignore */ }
  }, [dockOpen, isMobile]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activeView={activeView} onViewChange={onViewChange} isOpen={sidebarOpen} onOpenChange={setSidebarOpen} />

      <div className={cn('h-screen flex flex-col', !isMobile && 'ms-60', !isMobile && dockOpen && 'me-[22rem]')}>
        <Header
          isDark={isDark}
          onThemeToggle={() => setTheme(isDark ? 'light' : 'dark')}
          onMenuClick={() => setSidebarOpen(true)}
          onlineUsers={onlineUsers}
          onNavigate={onViewChange}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onNavigate={onViewChange} />

      {/* Ask Masira: docked on desktop, bottom sheet-style panel on mobile */}
      {dockOpen ? (
        <aside
          aria-label={t('Ask Masira')}
          className={cn(
            'fixed z-40 bg-card',
            isMobile ? 'inset-x-0 bottom-0 h-[70vh] border-t border-border shadow-2xl' : 'top-0 end-0 h-screen w-[22rem]',
          )}
        >
          <AIChat
            variant="dock"
            onClose={() => setDockOpen(false)}
            section={sectionForView(activeView)?.key}
            view={activeView}
            viewLabel={VIEW_LABELS[activeView]}
            canOpen={canAccess}
            onNavigate={onViewChange}
            startFlow={flowRequest}
          />
        </aside>
      ) : (
        <button
          type="button"
          onClick={() => setDockOpen(true)}
          aria-label={t('Open Ask Masira')}
          className="fixed bottom-4 end-4 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 font-heading text-sm font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />  {t('Ask Masira')}
        </button>
      )}
    </div>
  );
};

export default ConsoleLayout;
